import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import OpportunityDetails from './pages/OpportunityDetails';
import MySignups from './pages/MySignups';
import MyHours from './pages/MyHours';
import VolunteerProfile from './pages/VolunteerProfile';
import AdminDashboard from './pages/AdminDashboard';
import AdminReports from './pages/AdminReports';
import AdminEmailLogs from './pages/AdminEmailLogs';
import AdminIncidentReview from './pages/AdminIncidentReview';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerOpportunities from './pages/partner/PartnerOpportunities';
import PartnerOpportunityDetail from './pages/partner/PartnerOpportunityDetail';
import PartnerReports from './pages/partner/PartnerReports';
import BackgroundCheckManagement from './pages/partner/BackgroundCheckManagement';
import IncidentReportForm from './pages/partner/IncidentReportForm';
import AdminPartnerOrgs from './pages/AdminPartnerOrgs';
import PartnerInviteAccept from './pages/PartnerInviteAccept';
import VolunteerTimeLog from './pages/VolunteerTimeLog';
import TimeEntryApproval from './pages/partner/TimeEntryApproval';
import PartnerParticipants from './pages/partner/PartnerParticipants';
import PartnerInvites from './pages/partner/PartnerInvites';
import PartnerStaff from './pages/partner/PartnerStaff';
import PartnerCommunityOpportunities from './pages/partner/PartnerCommunityOpportunities';
import ParticipantDetail from './pages/ParticipantDetail';
import AdminViewAsParticipant from './pages/AdminViewAsParticipant';
import VerifyEmail from './pages/VerifyEmail';
import About from './pages/About';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const getRoleBasedHome = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'partner_admin' || user.role === 'partner_staff') return '/partner/dashboard';
    return '/';
  };

  const authContextValue = useMemo(() => ({ user, login, logout }), [user, login, logout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/register" element={!user ? <Register /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            
            {/* Volunteer Routes - Also accessible to Admin */}
            <Route path="/" element={user ? <Home /> : <Navigate to="/login" />} />
            <Route path="/opportunities" element={user ? <Home /> : <Navigate to="/login" />} />
            <Route path="/opportunity/:id" element={user ? <OpportunityDetails /> : <Navigate to="/login" />} />
            <Route path="/my-signups" element={user ? <MySignups /> : <Navigate to="/login" />} />
            <Route path="/my-hours" element={user ? <MyHours /> : <Navigate to="/login" />} />
            <Route path="/time-log" element={user ? <VolunteerTimeLog /> : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? <VolunteerProfile /> : <Navigate to="/login" />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/reports" element={user?.role === 'admin' ? <AdminReports /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/email-logs" element={user?.role === 'admin' ? <AdminEmailLogs /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/partner-organizations" element={user?.role === 'admin' ? <AdminPartnerOrgs /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/incidents" element={user?.role === 'admin' ? <AdminIncidentReview /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/participants/:id" element={user?.role === 'admin' || user?.role === 'partner_admin' ? <ParticipantDetail /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/admin/participants/:id/view-as" element={user?.role === 'admin' ? <AdminViewAsParticipant /> : <Navigate to={getRoleBasedHome()} />} />
            
            {/* Partner Routes */}
            <Route path="/partner/dashboard" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerDashboard /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/opportunities" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerOpportunities /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/opportunities/:id" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerOpportunityDetail /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/reports" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerReports /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/participants" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerParticipants /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/invites" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerInvites /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/staff" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerStaff /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/community" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <PartnerCommunityOpportunities /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/time-entries" element={user?.role === 'partner_admin' || user?.role === 'partner_staff' ? <TimeEntryApproval /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/background-checks" element={user?.role === 'partner_admin' ? <BackgroundCheckManagement /> : <Navigate to={getRoleBasedHome()} />} />
            <Route path="/partner/report-incident" element={user?.role === 'partner_admin' ? <IncidentReportForm /> : <Navigate to={getRoleBasedHome()} />} />
            
            {/* Partner Invite (Public) */}
            <Route path="/partner/invite/:token" element={<PartnerInviteAccept />} />
            
            {/* Public Routes */}
            <Route path="/about" element={<About />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;